/**
 * NotificationReportContentItem
 *
 * reportContent — `notification_report_content_item.dart:36-55`.
 * Leading: a `report.png` icon over a circular `backgroundColorLight` surface
 * (no avatar/user tap). Title: `Your {image|video} has been blocked because it
 * violated our Terms of Service!`. Subtitle: the report body + ` - {date}`.
 * Trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationLeadingIcon } from './shared/NotificationLeadingIcon';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import type { NotificationItemProps } from './notificationItemProps';

export const NotificationReportContentItem: React.FC<NotificationItemProps> = ({
  notification,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const { theme } = useTheme();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const mediaInfo = info.mediaInfo;
  const isVideo = MediaUtils.mediaInfoIsVideo(mediaInfo);

  const title = isVideo ? n.reportContentVideoTitle : n.reportContentImageTitle;
  const subtitle = `${n.reportContentSubtitle} - ${formatDate(info.timestamp)}`;

  return (
    <NotificationRowLayout
      leading={
        <NotificationLeadingIcon
          iconPath="/assets/images/report.png"
          iconColor={theme.disabled}
          circleBackground
        />
      }
      title={title}
      subtitle={subtitle}
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
